import { allowLinkingAiConfigsToViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Queryfilter as QueryFilter } from '@gonfalon/queryfilter/builder';
import { and, anyOf, equals, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';

import type { GetAIConfigsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export type GetAIConfigsQueryParams = Omit<GetAIConfigsOpenAPIQueryParams, 'filter' | 'sort'> & {
  sort?: 'name' | '-name' | 'creationDate' | '-creationDate' | 'lastModified' | '-lastModified';
  filter?: {
    query?: string;
    /** Filter by config keys: produces `key anyOf ["key1","key2"]` */
    key?: string[];
    tags?: string[];
    maintainerTeamKey?: string;
    maintainerId?: string;
    creationDate?: { before: number };
    view?: string;
    mode?: string[];
  };
};

export function querySerializer(queryParams: GetAIConfigsQueryParams) {
  const searchParams = new URLSearchParams();

  if (queryParams.filter) {
    const serializedFilter = serializeFilterParam(queryParams.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (queryParams.sort) {
    searchParams.set('sort', queryParams.sort);
  }

  if (queryParams.limit) {
    searchParams.set('limit', queryParams.limit.toString());
  }

  if (queryParams.offset) {
    searchParams.set('offset', queryParams.offset.toString());
  }

  return searchParams.toString();
}

function serializeFilterParam(value: NonNullable<GetAIConfigsQueryParams['filter']>) {
  const filter: QueryFilter[] = [];

  if (value.query) {
    filter.push(equals('query', value.query));
  }

  if (value.key?.length) {
    filter.push(anyOf('key', value.key));
  }

  if (value.tags?.length) {
    filter.push(anyOf('tags', value.tags));
  }

  if (value.maintainerId) {
    filter.push(equals('maintainerId', value.maintainerId));
  }

  if (value.maintainerTeamKey) {
    filter.push(equals('maintainerTeamKey', value.maintainerTeamKey));
  }

  if (value.creationDate) {
    filter.push(equals('creationDate', JSON.stringify(value.creationDate)));
  }

  if (value.view && allowLinkingAiConfigsToViews()) {
    filter.push(equals('view', value.view));
  }

  if (value.mode?.length) {
    filter.push(anyOf('mode', value.mode));
  }

  return queryfilterToString(and(filter));
}
